import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import {
  AttributionControl,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { useCart } from '../context/CartContext';
import { apiService } from '../services/api';
import { isCafeInactive } from '../utils/coffeeShop';
import { useCoffeeShops } from '../utils/coffeeShopsCache';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  TILE_SOURCES,
  readStoredUserLocation,
  saveUserLocation,
} from '../utils/mapPreload';
import { getWorkingHoursText } from '../utils/workingHours';
import NotificationBell from './NotificationBell';
import 'leaflet/dist/leaflet.css';
import '../styles/MapView.css';

const cafeIcon = L.divIcon({
  className: 'map-marker-wrap',
  html: '<div class="map-cafe-pin" aria-hidden="true"></div>',
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -34],
});

const inactiveCafeIcon = L.divIcon({
  className: 'map-marker-wrap',
  html: '<div class="map-cafe-pin map-cafe-pin--soon" aria-hidden="true"></div>',
  iconSize: [32, 40],
  iconAnchor: [16, 40],
  popupAnchor: [0, -34],
});

const userIcon = L.divIcon({
  className: 'map-marker-wrap',
  html: '<div class="map-user-dot" aria-hidden="true"><span></span></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -12],
});

function getDistance(coords1, coords2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const [lat1, lon1] = coords1;
  const [lat2, lon2] = coords2;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function hasCoordinates(shop) {
  return Array.isArray(shop?.coordinates) && shop.coordinates.length === 2;
}

function MapBinder({ mapRef, onReady, onMapClick, isActive }) {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
    onReady?.(map);
  }, [map, mapRef, onReady]);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    const resetView = () => {
      map.invalidateSize();
      map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, { animate: false });
    };
    resetView();
    const timer = window.setTimeout(resetView, 120);
    return () => window.clearTimeout(timer);
  }, [isActive, map]);

  useMapEvents({
    click: () => {
      onMapClick?.();
    },
  });

  return null;
}

function FallbackTileLayer() {
  const [sourceIndex, setSourceIndex] = useState(0);
  const errorsRef = useRef(0);
  const source = TILE_SOURCES[sourceIndex] || TILE_SOURCES[0];
  const tileOptions = {
    url: source.url,
    attribution: source.attribution,
    maxZoom: source.maxZoom,
    tileSize: 256,
    zoomOffset: 0,
    detectRetina: false,
    keepBuffer: 2,
    updateWhenIdle: false,
  };

  if (source.subdomains) {
    tileOptions.subdomains = source.subdomains;
  }

  return (
    <TileLayer
      key={source.url}
      {...tileOptions}
      eventHandlers={{
        tileerror: () => {
          errorsRef.current += 1;
          if (errorsRef.current >= 6 && sourceIndex < TILE_SOURCES.length - 1) {
            errorsRef.current = 0;
            setSourceIndex((current) => current + 1);
          }
        },
      }}
    />
  );
}

const MapView = ({ isActive = true }) => {
  const navigate = useNavigate();
  const { setSelectedCafe, carts } = useCart();
  const { shops, loading, error, refresh } = useCoffeeShops();
  const [selectedCafe, setSelectedCafeLocal] = useState(null);
  const storedLocation = useMemo(() => readStoredUserLocation(), []);
  const [userLocation, setUserLocation] = useState(storedLocation);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const mapRef = useRef(null);
  const markerRefs = useRef({});
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const skipNextMapClick = useRef(false);

  const totalCartItems = useMemo(() => {
    return Object.values(carts).reduce((total, cart) => {
      return total + (cart.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0);
    }, 0);
  }, [carts]);

  const coffeeShops = useMemo(
    () => (shops || []).filter(hasCoordinates),
    [shops],
  );

  const centerMapOn = useCallback((coords, zoom = 14) => {
    if (!mapRef.current || !coords) {
      return;
    }
    mapRef.current.setView(coords, zoom, { animate: true });
  }, []);

  const handleMapReady = useCallback(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, []);

  useEffect(() => {
    if (!navigator.geolocation) {
      return undefined;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = [position.coords.latitude, position.coords.longitude];
        setUserLocation(userCoords);
        saveUserLocation(userCoords);
      },
      (geoError) => {
        console.warn('Ошибка получения геолокации:', geoError.code, geoError.message);
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 5 * 60 * 1000,
      },
    );
    return undefined;
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return undefined;
    }

    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await apiService.searchCoffeeShops(searchQuery, 10);
        setSearchResults(results);
        setShowSearchResults(true);
      } catch (err) {
        console.error('Ошибка поиска:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSearchResults && !event.target.closest('.search-container')) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchResults]);

  const openShop = useCallback((shop, zoom = 15) => {
    if (!shop) {
      return;
    }
    setSelectedCafeLocal(shop);
    setSelectedCafe(shop);
    if (hasCoordinates(shop)) {
      centerMapOn(shop.coordinates, zoom);
      window.setTimeout(() => {
        markerRefs.current[shop.id]?.openPopup();
      }, 350);
    }
  }, [centerMapOn, setSelectedCafe]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (!value.trim()) {
      setShowSearchResults(false);
    }
  };

  const handleSearchResultClick = (shop) => {
    setSearchQuery(shop.name);
    setShowSearchResults(false);
    openShop(shop, 15);
    searchInputRef.current?.blur();
  };

  const handleCafeSelect = (shop) => {
    skipNextMapClick.current = true;
    setSelectedCafeLocal(shop);
    setSelectedCafe(shop);
  };

  const handleOpenMenuForSelected = () => {
    if (selectedCafe) {
      navigate(`/menu/${selectedCafe.id}`);
    }
  };

  const handleMapClick = () => {
    if (skipNextMapClick.current) {
      skipNextMapClick.current = false;
      return;
    }
    setSelectedCafeLocal(null);
    setSelectedCafe(null);
  };

  const handleNearbyClick = () => {
    const locateAndFocus = (userCoords) => {
      setUserLocation(userCoords);
      saveUserLocation(userCoords);
      centerMapOn(userCoords, 14);

      let closestCafe = null;
      let minDistance = Infinity;
      coffeeShops.forEach((shop) => {
        const distance = getDistance(userCoords, shop.coordinates);
        if (distance < minDistance) {
          minDistance = distance;
          closestCafe = shop;
        }
      });

      if (closestCafe) {
        window.setTimeout(() => openShop(closestCafe, 14), 500);
      }
    };

    if (userLocation) {
      locateAndFocus(userLocation);
      return;
    }

    if (!navigator.geolocation) {
      alert('Геолокация не поддерживается вашим браузером');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        locateAndFocus([position.coords.latitude, position.coords.longitude]);
      },
      (geoError) => {
        alert('Не удалось определить ваше местоположение');
        console.error(geoError);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      },
    );
  };

  return (
    <div className="map-container">
      <div className="header">
        <div className="flex space-between align-center">
          <h1 style={{ margin: 0 }}>RCoffee</h1>
          <div className="map-header-buttons">
            <NotificationBell />
            <button
              className="btn map-header-button"
              onClick={() => navigate('/profile')}
            >
              Профиль
            </button>
            <button
              className="btn map-header-button"
              onClick={() => navigate('/orders')}
            >
              Заказы
            </button>
          </div>
        </div>

        <div className="search-container map-search-container">
          <div className="map-search-wrapper">
            <input
              ref={searchInputRef}
              type="text"
              className="search-input map-search-input"
              placeholder="Поиск кофеен по названию или адресу..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => {
                if (searchResults.length > 0) {
                  setShowSearchResults(true);
                }
              }}
            />
            <span className="map-search-icon" />
            {isSearching && (
              <span className="map-search-loading">
                Поиск...
              </span>
            )}
          </div>

          {showSearchResults && searchResults.length > 0 && (
            <div className="search-results map-search-results">
              {searchResults.map((shop) => (
                <div
                  key={shop.id}
                  className="search-result-item map-search-result-item"
                  onClick={() => handleSearchResultClick(shop)}
                >
                  <div className="map-search-result-icon" />
                  <div className="map-search-result-content">
                    <div className="map-search-result-name">
                      <span>{shop.name}</span>
                      {isCafeInactive(shop) && (
                        <span className="map-search-soon-badge">Скоро</span>
                      )}
                    </div>
                    <div className="map-search-result-address">
                      {shop.address}
                    </div>
                    {shop.rating && (
                      <div className="map-search-result-rating">
                        ⭐ {shop.rating}
                      </div>
                    )}
                  </div>
                  <div className="map-search-result-arrow">
                    →
                  </div>
                </div>
              ))}
            </div>
          )}

          {showSearchResults && searchQuery.trim() && searchResults.length === 0 && !isSearching && (
            <div className="map-search-empty">
              Ничего не найдено
            </div>
          )}
        </div>
      </div>

      {error && coffeeShops.length === 0 && (
        <div className="map-inline-error">
          <span>{error}</span>
          <button type="button" className="btn map-inline-error-button" onClick={() => refresh()}>
            Повторить
          </button>
        </div>
      )}

      {loading && coffeeShops.length === 0 && (
        <div className="map-inline-hint">Загружаем кофейни...</div>
      )}

      <div className="map-leaflet-wrap">
        <MapContainer
          className="map-leaflet"
          style={{ height: '100%', width: '100%' }}
          center={DEFAULT_MAP_CENTER}
          zoom={DEFAULT_MAP_ZOOM}
          zoomControl={false}
          attributionControl={false}
          minZoom={3}
          maxZoom={19}
          preferCanvas
        >
          <MapBinder
            mapRef={mapRef}
            onReady={handleMapReady}
            onMapClick={handleMapClick}
            isActive={isActive}
          />
          <FallbackTileLayer />
          <AttributionControl position="bottomright" prefix={false} />

          {userLocation && (
            <Marker position={userLocation} icon={userIcon} zIndexOffset={1000}>
              <Popup>
                <strong>Ваше местоположение</strong>
                <div>Вы здесь</div>
              </Popup>
            </Marker>
          )}

          {coffeeShops.map((shop) => {
            const inactive = isCafeInactive(shop);
            return (
              <Marker
                key={shop.id}
                position={shop.coordinates}
                icon={inactive ? inactiveCafeIcon : cafeIcon}
                eventHandlers={{
                  click: () => handleCafeSelect(shop),
                }}
                ref={(instance) => {
                  if (instance) {
                    markerRefs.current[shop.id] = instance;
                  }
                }}
              >
                <Popup>
                  <div className="map-popup">
                    <div className="map-popup-title">
                      <strong>{shop.name}</strong>
                      {inactive && <span className="map-soon-badge">Скоро</span>}
                    </div>
                    <div>{shop.address}</div>
                    <div>🕐 {getWorkingHoursText(shop)}</div>
                    <div>⭐ {shop.rating ?? '—'} • {shop.distance ?? '—'}</div>
                    {inactive && (
                      <div className="map-popup-soon">
                        Заказ пока недоступен. Меню можно посмотреть.
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      <div className="nearby-button-container">
        <button className="nearby-button" onClick={handleNearbyClick}>
          Рядом со мной
        </button>
      </div>

      <button
        className="map-floating-cart-btn"
        onClick={() => navigate('/cart')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white" viewBox="0 0 16 16">
          <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 1 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 1 1 0-2z"/>
        </svg>
        {totalCartItems > 0 && (
          <span className="map-cart-badge">
            {totalCartItems > 99 ? '99+' : totalCartItems}
          </span>
        )}
      </button>

      {selectedCafe && (
        <div className="map-info">
          <div className="map-info-title-row">
            <h3>{selectedCafe.name}</h3>
            {isCafeInactive(selectedCafe) && (
              <span className="map-soon-badge">Скоро</span>
            )}
          </div>
          <p>{selectedCafe.address}</p>
          <p>
            🕐 {getWorkingHoursText(selectedCafe)} • ⭐ {selectedCafe.rating} •{' '}
            {selectedCafe.distance}
          </p>
          <button className="btn" type="button" onClick={handleOpenMenuForSelected}>
            {isCafeInactive(selectedCafe) ? 'Меню' : 'Заказать здесь'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MapView;
